import { useState, useEffect, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, Loader2, ChevronDown, X } from 'lucide-react';
import { devicesActions } from '@/store';
import api from '@/lib/api';
import useFeatures from '@/hooks/useFeatures';
import { showToast } from '@/components/shared/Toast';
import { parseFuelCalibration, validateCalibrationPoints } from '@/lib/calibrationUtils';

// Lazy: the editor's curve chart is Recharts, and this section sits in the
// vehicle drawer on the Live Map, which must not fetch Recharts (TASK-478).
const CalibrationEditor = lazy(() => import('@/features/fuel/CalibrationEditor'));

const UNITS = ['liters', 'gallons', 'percent'];

export default function FuelCalibrationSection({ device, isAdmin }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const allDevices = useSelector((state) => state.devices.items);
  const { fuel: fuelModule } = useFeatures();

  // Parse existing calibration from device attributes
  const existing = parseFuelCalibration(device?.attributes?.fuelCalibration);

  const [open, setOpen] = useState(false);
  const [unit, setUnit] = useState(existing?.unit || 'liters');
  const [points, setPoints] = useState(existing?.points || []);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Reset state when device changes
  useEffect(() => {
    const cal = parseFuelCalibration(device?.attributes?.fuelCalibration);
    setUnit(cal?.unit || 'liters');
    setPoints(cal?.points || []);
    setValidationErrors([]);
  }, [device?.id, device?.attributes?.fuelCalibration]);

  // Devices with calibration data for copy-from dropdown (excluding current device)
  const copyFromDevices = Object.values(allDevices).filter(
    (d) => d.id !== device?.id && d.attributes?.fuelCalibration != null
  );

  const handleCopyFrom = (e) => {
    const deviceId = parseInt(e.target.value, 10);
    if (!deviceId) return;
    const srcDevice = allDevices[deviceId];
    const cal = parseFuelCalibration(srcDevice?.attributes?.fuelCalibration);
    if (cal) {
      setUnit(cal.unit);
      setPoints([...cal.points]);
    }
    e.target.value = '';
  };

  const handleSave = async () => {
    const { valid, errors } = validateCalibrationPoints(points);
    if (!valid) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);
    setSaving(true);
    try {
      const newCalib = { unit, points };
      const updatedAttrs = { ...device.attributes, fuelCalibration: JSON.stringify(newCalib) };
      const updatedDevice = { ...device, attributes: updatedAttrs };
      const result = await api.updateDevice(device.id, updatedDevice);
      dispatch(devicesActions.update(result));
      showToast(t('fuelCalibration.saved'));
    } catch (err) {
      showToast(t('common.failedSave') + ': ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      const updatedAttrs = { ...device.attributes };
      delete updatedAttrs.fuelCalibration;
      const updatedDevice = { ...device, attributes: updatedAttrs };
      const result = await api.updateDevice(device.id, updatedDevice);
      dispatch(devicesActions.update(result));
      setPoints([]);
      setUnit('liters');
      showToast(t('fuelCalibration.cleared'));
    } catch (err) {
      showToast(t('common.failedSave') + ': ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin || !device?.id) return null;

  if (fuelModule) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-800/50 dark:text-slate-300">
        <span className="font-bold uppercase tracking-wider">{t('fuelCalibration.title')}</span>
        {' · '}
        <Link to="/fuel?view=setup" className="font-semibold text-blue-600 hover:underline dark:text-blue-400">
          {t('fuelCalibration.managedOnFuel', 'Managed on the Fuel page')}
        </Link>
      </div>
    );
  }

  const labelClass = 'block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1';
  const selectClass =
    'w-full h-8 px-2 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-500/20';

  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
          {t('fuelCalibration.title')}
        </span>
        <ChevronDown
          size={14}
          className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Body */}
      {open && (
        <div className="p-4 space-y-4">
          {/* Copy from device */}
          <div>
            <label className={labelClass}>{t('fuelCalibration.copyFrom')}</label>
            <select onChange={handleCopyFrom} defaultValue="" className={selectClass}>
              <option value="" disabled>
                {copyFromDevices.length === 0
                  ? t('fuelCalibration.noCopyDevices')
                  : t('fuelCalibration.copyFrom')}
              </option>
              {copyFromDevices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <Suspense fallback={<div className="h-40 flex items-center justify-center"><Loader2 size={14} className="animate-spin text-slate-400" /></div>}>
            <CalibrationEditor
              value={{ unit, points }}
              onChange={(v) => { setUnit(v.unit); setPoints(v.points); }}
              deviceName={device?.name}
              units={UNITS}
            />
          </Suspense>
          {validationErrors.map((err, i) => (
            <p key={i} className="text-[11px] text-red-500 dark:text-red-400">{err}</p>
          ))}

          {/* Save / Clear actions */}
          <div className="flex items-center gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="h-8 px-3 text-xs font-semibold rounded-lg flex items-center gap-1.5 text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {t('fuelCalibration.save')}
            </button>

            {existing && (
              <button
                type="button"
                onClick={handleClear}
                disabled={saving}
                className="h-8 px-3 text-xs font-semibold rounded-lg flex items-center gap-1.5 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/60 disabled:opacity-40"
              >
                <X size={12} />
                {t('fuelCalibration.clear')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
